import styled from 'styled-components';

import moment, { MomentInput } from 'moment-timezone';

import { Avatar, AVATAR_SIZES } from 'ui/avatar';
import { Tooltip } from 'ui/tooltip';
import { spacing, body2, caption, colors } from 'ui/theme';
import { useExperimentsTranslations, useProjectsTimeZone } from 'modules/experiments/hooks';
import { getFormattedDateWithHoursAndMins, getFormattedTime } from 'modules/experiments/utils';
import { TFunction } from 'react-i18next';

/**
 * Ported over from src/ui/indexPages/tables/cells/LastModifiedCell/index.tsx
 * We want to consistently show the project's timezone for experiments. This is not
 * doable in the current shared component.
 * Also, using translation service to render the text.
 *
 * Revert back to the global component once we start using project timezone
 */

const Cell = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
`;

const CellContent = styled.div`
  display: flex;
  flex-direction: row;
`;

const CellContentText = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: ${spacing.base};
`;

const CellContentTextHeader = styled.span`
  ${body2}
`;

const CellContentTextCaption = styled.span`
  ${caption}
  color: ${colors.textSecondary};
`;

type CreatedTooltipContentProps = {
  name: string;
  date: string | number;
  timezone: string;
};

/**
 * Ported over from utils/date
 *
 * We will be using the project's timezone
 * and translation service
 */
const displayDateWithTodayAndYesterdayPrefix = (
  t: TFunction,
  date: MomentInput,
  timezone: string,
) => {
  const TODAY = moment().clone().startOf('day');
  const YESTERDAY = moment().clone().subtract(1, 'days').startOf('day');

  const isToday = moment(date).isSame(TODAY, 'd');
  const isYesterday = moment(date).isSame(YESTERDAY, 'd');

  if (isToday) {
    return `${t('table.lastModifiedCell.today')}, ${getFormattedTime(date, timezone)}`;
  }
  if (isYesterday) {
    return `${t('table.lastModifiedCell.yesterday')}, ${getFormattedTime(date, timezone)}`;
  }
  return getFormattedDateWithHoursAndMins(date, timezone);
};

const CreatedTooltipContent = ({ date, timezone, name }: CreatedTooltipContentProps) => {
  const { t } = useExperimentsTranslations();
  return (
    <>
      {getFormattedDateWithHoursAndMins(date, timezone)}
      <br />
      {t('table.lastModifiedCell.by')}
      {name}
    </>
  );
};

type AvatarTooltipContentProps = {
  email: string;
  name: string;
};

const AvatarTooltipContent = ({ email, name }: AvatarTooltipContentProps) =>
  name.length ? (
    <>
      {name}
      <br />
      {email}
    </>
  ) : (
    <>{email}</>
  );

export type UserInfo = {
  emailAddress: string;
  fullName?: string;
  avatarUrl?: string;
};

export type LastModifiedCellProps = {
  createdAt: string | number;
  createdBy: UserInfo;
  modifiedAt?: string | number;
  modifiedBy?: UserInfo;
  showAvatar?: boolean;
};

export const LastModifiedCell = ({
  createdBy,
  createdAt,
  modifiedBy,
  modifiedAt,
  showAvatar = true,
  ...otherProps
}: LastModifiedCellProps) => {
  const { t } = useExperimentsTranslations();
  const timezone = useProjectsTimeZone();
  const modifier = modifiedBy || createdBy;
  const [modifierFirstName, modifierLastName] = modifier.fullName?.split(' ') || [
    modifier.emailAddress,
    '',
  ];

  return (
    <Cell {...otherProps}>
      <CellContent>
        {showAvatar && (
          <Tooltip
            tooltipContent={
              <AvatarTooltipContent email={modifier.emailAddress} name={modifier.fullName || ''} />
            }
          >
            <Avatar
              size={AVATAR_SIZES.MEDIUM}
              firstName={modifierFirstName}
              lastName={modifierLastName}
              imgSrc={modifier.avatarUrl}
            />
          </Tooltip>
        )}
        <CellContentText>
          <CellContentTextHeader>
            {displayDateWithTodayAndYesterdayPrefix(t, modifiedAt || createdAt, timezone)}
          </CellContentTextHeader>
          <CellContentTextCaption>
            {t('table.lastModifiedCell.created')}
            <Tooltip
              isUnderlined
              tooltipContent={
                <CreatedTooltipContent
                  date={createdAt}
                  timezone={timezone}
                  name={createdBy.fullName || createdBy.emailAddress}
                />
              }
            >
              {moment(createdAt).fromNow()}
            </Tooltip>
          </CellContentTextCaption>
        </CellContentText>
      </CellContent>
    </Cell>
  );
};
